import { useEffect, useState } from 'react';
import { Search, Filter, Grid, List } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TourCard from '../components/TourCard';
import { publicApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

export default function ToursPage() {
  const [tours, setTours] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const { t } = useLanguage();

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const [toursRes, settingsRes] = await Promise.all([
          publicApi.getTours(),
          publicApi.getSettings()
        ]);
        setTours(toursRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  const categories = [...new Set(tours.map(t => t.category))];

  const filteredTours = tours.filter(tour => {
    const matchesSearch = tour.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tour.short_description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || tour.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tours-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">
              {t('tours_title')}
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-4">
              {t('tours_subtitle')}
            </h1>
            <p className="text-stone-500 max-w-2xl mx-auto">
              {t('tours_description')}
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder={t('tours_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 rounded-xl" data-testid="category-filter">
                <SelectValue placeholder={t('tours_filter_all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tours_filter_all')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl h-96 skeleton" />
              ))}
            </div>
          ) : filteredTours.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-stone-500 text-lg">{t('tours_no_results')}</p>
              <Button 
                variant="link" 
                onClick={() => { setSearchTerm(''); setCategoryFilter('all'); }}
                className="mt-2"
              >
                {t('tours_clear_filters')}
              </Button>
            </div>
          ) : (
            <>
              <p className="text-stone-500 mb-6">
                {filteredTours.length} {t('tours_found')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                {filteredTours.map((tour, index) => (
                  <div 
                    key={tour.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <TourCard 
                      tour={tour} 
                      whatsappNumber={settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567'}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
